import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Grid,
  CardContent,
  Avatar,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Divider,
  alpha,
  useTheme,
} from '@mui/material';
// Using new design system components
import { Button, Typography, Card } from '../../../design-system';
import { DESIGN_TOKENS } from '../../../design-system/tokens/colors';
import {
  TrendingUp,
  TrendingDown,
  AccountBalanceWallet,
  Receipt,
  MonetizationOn,
  Warning,
  CheckCircle,
  AttachMoney,
  Assessment,
  Agriculture,
} from '@mui/icons-material';

interface FinancialDashboardProps {
  tenantId?: string;
  farmId?: string;
}

// Mock financial data
const mockFinancialData = {
  summary: {
    total_income: 45000,
    total_expenses: 28500,
    net_profit: 16500,
    pending_receivables: 8200,
    pending_payables: 3500,
    monthly_trend: [
      { month: 'Nov', income: 38000, expenses: 25000 },
      { month: 'Dez', income: 42000, expenses: 26500 },
      { month: 'Jan', income: 45000, expenses: 28500 }
    ]
  },
  invoices: {
    total_invoices: 24,
    paid_invoices: 20,
    overdue_invoices: 2,
    total_amount_due: 11700
  },
  recentTransactions: [
    {
      id: 'trans-001',
      transaction_date: '2024-01-25',
      description: 'Venda de leite - Lote Janeiro',
      category: 'milk_sale',
      type: 'income' as const,
      amount: 18500,
      reference_number: 'VL-2024-001'
    },
    {
      id: 'trans-002', 
      transaction_date: '2024-01-23',
      description: 'Compra de ração premium',
      category: 'feed_purchase',
      type: 'expense' as const,
      amount: 5200,
      reference_number: 'CP-2024-015'
    },
    {
      id: 'trans-003',
      transaction_date: '2024-01-20',
      description: 'Venda de animais - 3 cabeças',
      category: 'cattle_sale',
      type: 'income' as const,
      amount: 12000,
      reference_number: 'VA-2024-003'
    },
    {
      id: 'trans-004',
      transaction_date: '2024-01-18',
      description: 'Consulta veterinária',
      category: 'veterinary',
      type: 'expense' as const,
      amount: 850,
      reference_number: 'VET-2024-007'
    },
    {
      id: 'trans-005',
      transaction_date: '2024-01-15',
      description: 'Combustível e manutenção',
      category: 'equipment',
      type: 'expense' as const,
      amount: 1200,
      reference_number: 'MAN-2024-002'
    }
  ]
};

export const FinancialDashboard: React.FC<FinancialDashboardProps> = ({
  tenantId,
  farmId,
}) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Simulate loading
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const getTransactionIcon = (category: string) => {
    const iconMap: Record<string, React.ReactElement> = {
      milk_sale: <MonetizationOn />,
      cattle_sale: <AttachMoney />,
      feed_purchase: <Receipt />,
      veterinary: <Receipt />,
      equipment: <Receipt />,
      default: <Receipt />,
    };
    return iconMap[category] || iconMap.default;
  };

  const getTransactionColor = (type: 'income' | 'expense') => {
    return type === 'income' ? DESIGN_TOKENS.colors.status.success[500] : DESIGN_TOKENS.colors.status.error[500];
  };

  const getCategoryLabel = (category: string) => {
    const labels: Record<string, string> = {
      milk_sale: 'Venda de Leite',
      cattle_sale: 'Venda de Gado',
      feed_purchase: 'Compra de Ração',
      veterinary: 'Veterinário',
      equipment: 'Equipamentos',
    };
    return labels[category] || category;
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography variant="body2" sx={{ mt: 2, textAlign: 'center' }}>
          Carregando dados financeiros...
        </Typography>
      </Box>
    );
  }

  const { summary, invoices, recentTransactions } = mockFinancialData;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Avatar sx={{ bgcolor: DESIGN_TOKENS.colors.organization.farm, width: 48, height: 48 }}>
            <Assessment />
          </Avatar>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: DESIGN_TOKENS.colors.organization.farm, mb: 0.5 }}>
              Gestão Financeira
            </Typography>
            <Typography variant="body1" color="secondary">
              Controle completo das finanças da fazenda
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ height: '100%', border: '2px solid transparent', '&:hover': { borderColor: DESIGN_TOKENS.colors.status.success[500] } }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ bgcolor: alpha(DESIGN_TOKENS.colors.status.success[500], 0.1), color: DESIGN_TOKENS.colors.status.success[500], mr: 2 }}>
                  <TrendingUp />
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: DESIGN_TOKENS.colors.status.success[500] }}>
                    {formatCurrency(summary.total_income)}
                  </Typography>
                  <Typography variant="body2" color="secondary">
                    Receitas Totais
                  </Typography>
                </Box>
              </Box>
              <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.status.success[500], fontWeight: 600 }}>
                ↗ +12% vs mês anterior
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ height: '100%', border: '2px solid transparent', '&:hover': { borderColor: DESIGN_TOKENS.colors.status.error[500] } }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ bgcolor: alpha(DESIGN_TOKENS.colors.status.error[500], 0.1), color: DESIGN_TOKENS.colors.status.error[500], mr: 2 }}>
                  <TrendingDown />
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: DESIGN_TOKENS.colors.status.error[500] }}>
                    {formatCurrency(summary.total_expenses)}
                  </Typography>
                  <Typography variant="body2" color="secondary">
                    Despesas Totais
                  </Typography>
                </Box>
              </Box>
              <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.status.error[500], fontWeight: 600 }}>
                ↗ +8% vs mês anterior
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ height: '100%', border: '2px solid transparent', '&:hover': { borderColor: summary.net_profit >= 0 ? DESIGN_TOKENS.colors.status.success[500] : DESIGN_TOKENS.colors.status.error[500] } }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ 
                  bgcolor: alpha(summary.net_profit >= 0 ? DESIGN_TOKENS.colors.status.success[500] : DESIGN_TOKENS.colors.status.error[500], 0.1), 
                  color: summary.net_profit >= 0 ? DESIGN_TOKENS.colors.status.success[500] : DESIGN_TOKENS.colors.status.error[500], 
                  mr: 2 
                }}>
                  <Assessment />
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ 
                    fontWeight: 700, 
                    color: summary.net_profit >= 0 ? DESIGN_TOKENS.colors.status.success[500] : DESIGN_TOKENS.colors.status.error[500] 
                  }}>
                    {formatCurrency(summary.net_profit)}
                  </Typography>
                  <Typography variant="body2" color="secondary">
                    Lucro Líquido
                  </Typography>
                </Box>
              </Box>
              <Typography variant="caption" sx={{ 
                color: summary.net_profit >= 0 ? DESIGN_TOKENS.colors.status.success[500] : DESIGN_TOKENS.colors.status.error[500], 
                fontWeight: 600 
              }}>
                {summary.net_profit >= 0 ? '↗' : '↘'} Margem: {((summary.net_profit / summary.total_income) * 100).toFixed(1)}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ height: '100%', border: '2px solid transparent', '&:hover': { borderColor: DESIGN_TOKENS.colors.status.warning[500] } }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ bgcolor: alpha(DESIGN_TOKENS.colors.status.warning[500], 0.1), color: DESIGN_TOKENS.colors.status.warning[500], mr: 2 }}>
                  <AccountBalanceWallet />
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: DESIGN_TOKENS.colors.status.warning[500] }}>
                    {formatCurrency(summary.pending_receivables)}
                  </Typography>
                  <Typography variant="body2" color="secondary">
                    A Receber
                  </Typography>
                </Box>
              </Box>
              <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.status.warning[500], fontWeight: 600 }}>
                {invoices.overdue_invoices} faturas em atraso
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts and Analysis */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              Status das Faturas
            </Typography>
            
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={4}>
                <Box sx={{ textAlign: 'center' }}>
                  <Avatar sx={{ bgcolor: alpha(DESIGN_TOKENS.colors.status.success[500], 0.1), color: DESIGN_TOKENS.colors.status.success[500], mx: 'auto', mb: 1 }}>
                    <CheckCircle />
                  </Avatar>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: DESIGN_TOKENS.colors.status.success[500] }}>
                    {invoices.paid_invoices}
                  </Typography>
                  <Typography variant="caption" color="secondary">
                    Pagas
                  </Typography>
                </Box>
              </Grid>
              
              <Grid size={4}>
                <Box sx={{ textAlign: 'center' }}>
                  <Avatar sx={{ bgcolor: alpha(DESIGN_TOKENS.colors.status.warning[500], 0.1), color: DESIGN_TOKENS.colors.status.warning[500], mx: 'auto', mb: 1 }}>
                    <Receipt />
                  </Avatar>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: DESIGN_TOKENS.colors.status.warning[500] }}>
                    {invoices.total_invoices - invoices.paid_invoices - invoices.overdue_invoices}
                  </Typography>
                  <Typography variant="caption" color="secondary">
                    Pendentes
                  </Typography>
                </Box>
              </Grid>

              <Grid size={4}>
                <Box sx={{ textAlign: 'center' }}>
                  <Avatar sx={{ bgcolor: alpha(DESIGN_TOKENS.colors.status.error[500], 0.1), color: DESIGN_TOKENS.colors.status.error[500], mx: 'auto', mb: 1 }}>
                    <Warning />
                  </Avatar>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: DESIGN_TOKENS.colors.status.error[500] }}>
                    {invoices.overdue_invoices}
                  </Typography>
                  <Typography variant="caption" color="secondary">
                    Em Atraso
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="secondary">
                Total em Aberto:
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600, color: DESIGN_TOKENS.colors.status.warning[500] }}>
                {formatCurrency(invoices.total_amount_due)}
              </Typography>
            </Box>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              Tendência Mensal
            </Typography>
            
            {summary.monthly_trend.map((month, index) => (
              <Box key={index} sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {month.month}
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    fontWeight: 600,
                    color: month.income - month.expenses >= 0 ? DESIGN_TOKENS.colors.status.success[500] : DESIGN_TOKENS.colors.status.error[500]
                  }}>
                    {formatCurrency(month.income - month.expenses)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" color="secondary">
                      Receitas: {formatCurrency(month.income)}
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={100}
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        backgroundColor: alpha(DESIGN_TOKENS.colors.status.success[500], 0.2),
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: DESIGN_TOKENS.colors.status.success[500],
                        },
                      }}
                    />
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" color="secondary">
                      Despesas: {formatCurrency(month.expenses)}
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={(month.expenses / month.income) * 100}
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        backgroundColor: alpha(DESIGN_TOKENS.colors.status.error[500], 0.2),
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: DESIGN_TOKENS.colors.status.error[500],
                        },
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            ))}
          </Paper>
        </Grid>
      </Grid>

      {/* Recent Transactions */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Transações Recentes
          </Typography>
          <Button variant="secondary" size="small" sx={{ borderColor: DESIGN_TOKENS.colors.organization.farm, color: DESIGN_TOKENS.colors.organization.farm }}>
            Ver Todas
          </Button>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Data</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Descrição</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Categoria</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Tipo</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Valor</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recentTransactions.map((transaction) => (
                <TableRow key={transaction.id} hover sx={{ '&:hover': { backgroundColor: alpha(DESIGN_TOKENS.colors.organization.farm, 0.05) } }}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {formatDate(transaction.transaction_date)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar sx={{ 
                        bgcolor: alpha(getTransactionColor(transaction.type), 0.1),
                        color: getTransactionColor(transaction.type),
                        mr: 2,
                        width: 32,
                        height: 32,
                      }}>
                        {getTransactionIcon(transaction.category)}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {transaction.description}
                        </Typography>
                        {transaction.reference_number && (
                          <Typography variant="caption" color="secondary">
                            Ref: {transaction.reference_number}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getCategoryLabel(transaction.category)}
                      size="small"
                      variant="outlined"
                      sx={{ 
                        borderColor: alpha(getTransactionColor(transaction.type), 0.3),
                        color: getTransactionColor(transaction.type)
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={transaction.type === 'income' ? 'Receita' : 'Despesa'}
                      size="small"
                      sx={{
                        bgcolor: alpha(getTransactionColor(transaction.type), 0.1),
                        color: getTransactionColor(transaction.type),
                        fontWeight: 600,
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 700,
                        color: getTransactionColor(transaction.type),
                        fontSize: '1rem'
                      }}
                    >
                      {transaction.type === 'income' ? '+' : '-'}
                      {formatCurrency(transaction.amount)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};
